import { z } from 'zod';

import { DOCUMENT_STATUSES } from '@/lib/document-status';
import { lmsSyncSchema } from '@/features/document-registration/types';
import { documentCapabilitiesSchema } from '@/lib/document-capabilities';

// Contrato del expediente (Ficha / Expediente) validado con Zod (ENG-VAL-01): un
// drift del backend (enum fuera de unión, campo faltante, timeline mal formado) se
// detecta acá y cae al estado de error de la query, no revienta en render.

const RESPONSIBILITY_ROLES = ['elaborador', 'revisor', 'aprobador', 'capacitador'] as const;

export const dossierPersonSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullable(),
  initials: z.string().nullable(),
});

export const dossierEntrySchema = z.object({
  id: z.string(),
  /** Empresa del documento: un actor cross-company puede ver fichas de otra empresa. */
  companyId: z.string(),
  code: z.string(),
  title: z.string().nullable(),
  status: z.enum(DOCUMENT_STATUSES),
  origin: z.string(),
  version: z.number().nullable(),
  documentType: z.object({ id: z.string(), name: z.string() }),
  processArea: z.object({ id: z.string(), name: z.string() }),
  effectiveDate: z.string().nullable(),
  nextReviewAt: z.string().nullable(),
  createdAt: z.string(),
  /** Tiene su documento del LMS (archivo) vinculado. */
  lmsLinked: z.boolean(),
  /** Dueño de proceso (G3-3); null = sin asignar o dado de baja. */
  owner: dossierPersonSchema.nullable(),
});

// Responsable por rol: persona del QMS o puesto del LMS (spec 2026-10-14 §1). `id` es
// la asignación (para quitarla); `displayName` es el nombre de la persona o del puesto.
export const dossierResponsibilitySchema = z.object({
  id: z.string(),
  role: z.enum(RESPONSIBILITY_ROLES),
  kind: z.enum(['user', 'position']),
  userId: z.string().nullable(),
  positionId: z.string().nullable(),
  positionName: z.string().nullable(),
  displayName: z.string().nullable(),
  initials: z.string().nullable(),
  /** Puede leer el tipo del documento (spec 2026-10-14 §8 D7); un puesto, siempre. */
  typeAccess: z.boolean(),
});

export const dossierNormSchema = z.object({
  id: z.string(),
  normCode: z.string(),
  clause: z.string(),
  requirement: z.string(),
});

const timelineTransitionSchema = z.object({
  kind: z.literal('transition'),
  at: z.string(),
  version: z.number(),
  fromStatus: z.enum(DOCUMENT_STATUSES).nullable(),
  toStatus: z.enum(DOCUMENT_STATUSES),
  actor: dossierPersonSchema.nullable(),
  isReproceso: z.boolean(),
  reason: z.string().nullable(),
});

const timelineSignatureSchema = z.object({
  kind: z.literal('signature'),
  at: z.string(),
  version: z.number(),
  purpose: z.string(),
  signer: dossierPersonSchema,
  ip: z.string().nullable(),
});

export const dossierTimelineItemSchema = z.discriminatedUnion('kind', [
  timelineTransitionSchema,
  timelineSignatureSchema,
]);

/** G2: una firma de aprobación de la versión efectiva. */
const dossierSealSignerSchema = z.object({
  signatureId: z.string(),
  signer: dossierPersonSchema,
  signedAt: z.string(),
});

export const dossierSealSchema = z
  .object({
    signatureId: z.string(),
    signer: dossierPersonSchema,
    signedAt: z.string(),
    version: z.number(),
    /** G2 (A-04): todas las firmas que aprobaron la versión, en orden. */
    signers: z.array(dossierSealSignerSchema),
    /** G2: firmas de rondas anteriores, sin efecto por devolución. */
    voided: z.array(dossierSealSignerSchema),
  })
  .nullable();

/** Campo propio del documento con su valor (QA F1 #14). */
export const dossierCustomFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  dataType: z.string(),
  options: z.array(z.string()).nullable(),
  required: z.boolean(),
  value: z.unknown(),
});

export const dossierSchema = z.object({
  entry: dossierEntrySchema,
  responsibilities: z.array(dossierResponsibilitySchema),
  norms: z.array(dossierNormSchema),
  customFields: z.array(dossierCustomFieldSchema).default([]),
  timeline: z.array(dossierTimelineItemSchema),
  seal: dossierSealSchema,
  /** Editar en línea, imprimir y comentar sobre ESTE documento (D6). */
  documentCapabilities: documentCapabilitiesSchema,
  /** Envíos al DMS del LMS (G3-4/G3-5); un proveedor los recibe vacíos. */
  lmsSync: lmsSyncSchema,
});

export const dossierResponseSchema = z.object({
  success: z.literal(true),
  data: dossierSchema,
});

export type DossierPerson = z.infer<typeof dossierPersonSchema>;
export type DossierEntry = z.infer<typeof dossierEntrySchema>;
export type DossierResponsibility = z.infer<typeof dossierResponsibilitySchema>;
export type DossierNorm = z.infer<typeof dossierNormSchema>;
export type DossierTimelineItem = z.infer<typeof dossierTimelineItemSchema>;
export type DossierSeal = z.infer<typeof dossierSealSchema>;
export type Dossier = z.infer<typeof dossierSchema>;

export const RESPONSIBILITY_ROLE_LABELS: Record<(typeof RESPONSIBILITY_ROLES)[number], string> = {
  elaborador: 'Elaborador',
  revisor: 'Revisor',
  aprobador: 'Aprobador',
  capacitador: 'Capacitador',
};
